/*
 * File: RefundSummary.tsx
 * Author: Austin Robinson
 * Date Created: 03/11/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useEffect, useMemo, useState } from 'react';

import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useFormContext, useWatch } from 'react-hook-form';

import { useGetStudentLedger } from 'api/hooks/private/users/students/GetStudentLedger';

import FinanceSummaryItem from 'components/Dialogs/components/FinanceSummaryItem';

import { formatCents } from 'utils/formatCurrency';

import { RefundFormValues } from '../RefundSchema';

function RefundSummary() {
  // projected student balance after refund
  const [newBalance, setNewBalance] = useState(0);
  const { watch } = useFormContext<RefundFormValues>();

  // fetch student's ledger data for balance calculation
  const { data: studentLedger } = useGetStudentLedger({
    variables: {
      id: watch('student_user_id'),
      showVoided: true,
    },
    initialData: [],
  });

  // get refund allocations from form
  // const refundAllocations = watch('allocations') ?? [];
  const refundAllocations = useWatch({
    name: 'allocations',
    defaultValue: [],
  }) as RefundFormValues['allocations'];

  // calculate the total refund amount
  const refundTotal = useMemo(() => {
    return refundAllocations.reduce((total: number, allocation) => total + (allocation?.amount ?? 0), 0);
  }, [refundAllocations]);
  // const refundTotal = refundAllocations.reduce((total: number, allocation) => total + (allocation?.amount ?? 0), 0);

  useEffect(() => {
    // wait until the ledger is fetched
    if (!studentLedger) return;

    // calculate the new account balance excluding the current charge
    const currentRefundId = watch('id');
    const prevRefundAmount = Number(studentLedger?.find((entry) => entry.id === currentRefundId)?.amount ?? 0);
    const currentBalance = Number(studentLedger[studentLedger.length - 1]?.balance ?? 0);

    setNewBalance(currentBalance - prevRefundAmount + refundTotal);
  }, [refundTotal, studentLedger, watch]);

  return (
    <Grid container rowSpacing={0} columnSpacing={3}>
      <FinanceSummaryItem
        title="Refund Amount"
        value={formatCents(refundTotal)}
        // error={!!errors["amount"]}
      />
      <Grid size={12} py={1}>
        <Divider />
      </Grid>
      <FinanceSummaryItem title="New Student Balance" value={formatCents(newBalance)} />
    </Grid>
  );
}

export default RefundSummary;
